import { useStaticQuery, graphql } from 'gatsby'
import PropTypes from 'prop-types'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useLocation } from '@gatsbyjs/reach-router'

interface MetaData {
  title: string
  description: string
  lang?: string
  keywords?: Array<string>
  canonical?: string
  children: any
  image?: string
  imageAlt?: string
  publishedDate?: string
  geo?: {
    placename: string
    position: string
    region: string
  }
  type?: string
  author?: string
}

function MetaData({
  description,
  lang = 'en',
  keywords = [],
  title,
  canonical,
  children,
  image,
  imageAlt,
  publishedDate,
  geo,
  type = 'website',
  author,
}: MetaData) {
  const location = useLocation()
  const { site } = useStaticQuery(graphql`
    query DefaultSEOQuery {
      site {
        siteMetadata {
          title
          description
          twitterUsername
          url
          image
        }
      }
    }
  `)

  const metaDescription = description || site.siteMetadata.description
  let metaImage
  if (!image) {
    metaImage = `${site.siteMetadata.url}${site.siteMetadata.image}`
  } else if (image.indexOf('https://') !== -1) {
    metaImage = image
  } else {
    metaImage = `${site.siteMetadata.url}/${image}`
  }
  const metaImageAlt = imageAlt || site.siteMetadata.description

  const metaArray = [
    {
      name: 'description',
      content: metaDescription,
    },
    {
      property: 'og:site_name',
      content: site.siteMetadata.title,
    },
    {
      property: 'og:title',
      content: title,
    },
    {
      property: 'og:description',
      content: metaDescription,
    },
    {
      property: 'og:type',
      content: type,
    },
    {
      property: 'og:url',
      content: location?.href,
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:creator',
      content: site.siteMetadata.twitterUsername,
    },
    {
      name: 'twitter:site',
      content: site.siteMetadata.twitterUsername,
    },
    {
      property: 'og:image',
      content: metaImage,
    },
    {
      property: 'image',
      content: metaImage,
    },
    {
      property: 'og:image:alt',
      content: metaImageAlt,
    },
    {
      property: 'og:publish_date',
      content: publishedDate,
    },
    {
      property: 'og:author',
      content: author,
    },
  ].concat(
    keywords && keywords.length > 0
      ? {
        name: 'keywords',
        content: keywords.join(', '),
      }
      : [],
  )

  if (geo) {
    if (geo.region) metaArray.push({ property: 'geo.region', content: geo.region })
    if (geo.placename) metaArray.push({ property: 'geo.placename', content: geo.placename })
    if (geo.position) metaArray.push({ property: 'geo.position', content: geo.position })
  }

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      meta={metaArray}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      defer={false}
    >
      {canonical && <link href={canonical} rel="canonical" />}
      {children}

      {/* <!-- Crisp Chat --> */}
      <script type="text/javascript">
        {`
          var init=function(){setTimeout(function(){window.$crisp=[],window.CRISP_WEBSITE_ID="8d39cddd-9a5c-4a2c-8b08-8b404a966a53",d=document,s=d.createElement("script"),s.src="https://client.crisp.chat/l.js",s.defer=1,d.getElementsByTagName("head")[0].appendChild(s)},3000)};"loading"!==document.readyState?init():document.addEventListener("DOMContentLoaded",function(){init()});
        `}
      </script>
    </Helmet>
  )
}

MetaData.propTypes = {
  description: PropTypes.string,
  keywords: PropTypes.arrayOf(PropTypes.string),
  lang: PropTypes.string,
  canonical: PropTypes.string,
  meta: PropTypes.array,
  children: PropTypes.node,
  image: PropTypes.node,
  imageAlt: PropTypes.node,
  title: PropTypes.string.isRequired,
}

export default MetaData
